import React, { useMemo, useRef, useContext } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import PieChart, {
    Series,
    Label,
    Connector,
    Legend,
    Tooltip,
    Export,
    Title,
    Font,
} from 'devextreme-react/pie-chart';
import { useGenderData } from '../contexts';
import { GenderFilterControls } from './GenderFilterControls';
import { DashboardCard, DashboardCardContext } from '../components/DashboardCard';

interface AggregatedGender {
    genderName: string;
    studentCount: number;
    percentage: number;
}

const GenderChartContent = ({ chartRef }: { chartRef: React.RefObject<PieChart> }) => {
    const { data, loading, error, grandTotal, filter } = useGenderData();
    const { isExpanded } = useContext(DashboardCardContext);

    const styleConfig = isExpanded ? {
        centerTitleSize: 42,
        centerSubSize: 18,
        innerRadius: 0.68,
        labelSize: 14
    } : {
        centerTitleSize: 32,
        centerSubSize: 14,
        innerRadius: 0.65,
        labelSize: 12
    };

    // Prepare metadata for export
    const exportMetadata = useMemo(() => {
        const yearsLabel = filter.schoolYears.length > 0
            ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
            : 'All Years';
        const exportDate = new Date().toLocaleDateString();
        
        return {
            title: 'Gender Distribution',
            subtitle: `Years: ${yearsLabel} | Exported: ${exportDate}`
        };
    }, [filter.schoolYears]);

    // Aggregate data across all selected years
    const aggregatedData = useMemo((): AggregatedGender[] => {
        if (!data || data.length === 0) return [];

        const genderMap = new Map<string, { count: number; displayOrder: number }>();

        data.forEach(item => {
            const existing = genderMap.get(item.genderName);
            if (existing) {
                existing.count += item.studentCount;
            } else {
                genderMap.set(item.genderName, {
                    count: item.studentCount,
                    displayOrder: item.genderDisplayOrder
                });
            }
        });

        const total = grandTotal?.totalStudents || 0;

        return Array.from(genderMap.entries())
            .map(([genderName, { count, displayOrder }]) => ({
                genderName,
                studentCount: count,
                percentage: total > 0 ? Math.round((count / total) * 100 * 10) / 10 : 0,
                displayOrder
            }))
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }, [data, grandTotal]);

    const totalStudents = grandTotal?.totalStudents || 0;

    if (loading) {
        return <Skeleton variant="rounded" height={400} />;
    }

    if (error) {
        return (
            <Typography color="error" align="center">
                {error}
            </Typography>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Typography align="center" color="textSecondary">
                No gender data available for selected years
            </Typography>
        );
    }

    const formatPercentage = (percent: number): string => {
        const pct = percent * 100;
        if (pct > 0 && pct < 1) return '<1';
        return Math.round(pct).toString();
    };

    const centerTemplate = () => {
        return (
            <svg>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="45%" 
                    style={{ 
                        fontSize: `${styleConfig.centerTitleSize}px`, 
                        fontWeight: 'bold',
                        fill: '#000'
                    }}
                >
                    {totalStudents.toLocaleString()}
                </text>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="55%" 
                    style={{ 
                        fontSize: `${styleConfig.centerSubSize}px`,
                        fill: '#666'
                    }}
                >
                    Total Students
                </text>
            </svg>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <GenderFilterControls />
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <PieChart
                    ref={chartRef}
                    dataSource={aggregatedData}
                    type="doughnut"
                    innerRadius={styleConfig.innerRadius}
                    height="100%"
                    palette={['#0D47A1', '#00897B', '#FFA726', '#AB47BC']}
                    centerRender={centerTemplate}
                >
                    <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
                        <Font size={18} weight={600} />
                    </Title>
                    <Series
                        argumentField="genderName"
                        valueField="studentCount"
                    >
                        <Label
                            visible={true}
                            position="outside"
                            customizeText={(pointInfo: { argumentText: string; valueText: string; percent: number }) => {
                                const displayPct = formatPercentage(pointInfo.percent);
                                return `${pointInfo.argumentText}\n${pointInfo.valueText} (${displayPct}%)`;
                            }}
                        >
                            <Font size={styleConfig.labelSize} />
                            <Connector visible={true} width={1} />
                        </Label>
                    </Series>
                    <Legend visible={false} />
                    <Tooltip
                        enabled={true}
                        customizeTooltip={(pointInfo: { argumentText: string; valueText: string; percent: number }) => {
                            const displayPct = formatPercentage(pointInfo.percent);
                            return {
                                text: `${pointInfo.argumentText}: ${pointInfo.valueText} (${displayPct}%)`
                            };
                        }}
                    />
                    <Export enabled={true} />
                </PieChart>
            </Box>
        </Box>
    );
};

export const GenderChartCard = () => {
    const { filter } = useGenderData();
    const chartRef = useRef<PieChart>(null);

    const handleExport = () => {
        if (chartRef.current) {
            const yearsLabel = filter.schoolYears.length > 0
                ? filter.schoolYears.join('_')
                : 'AllYears';
            chartRef.current.instance.exportTo(`Gender_${yearsLabel}`, 'png');
        }
    };

    return (
        <DashboardCard title="Gender" onExport={handleExport}>
            <GenderChartContent chartRef={chartRef} />
        </DashboardCard>
    );
};